// Admin User Management Page with Add User & Password Reset
// File: frontend/src/pages/AdminUsersPage.js

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../components/Layout.css';
import { isAthlete } from '../utils/roles';
import Button from '../components/Button';
import AdminPage from '../components/AdminShell';

const ROLE_LABELS = {
  athlete: 'Athlete',
  base_user: 'Base User',
  player_parent: 'Athlete/Parent',
  coach: 'Coach',
  basic_coach: 'Coach (Pending)',
  partner: 'Partner',
  guardian: 'Guardian/Parent',
  admin: 'Admin',
  internal_staff: 'Internal Staff'
};

// Role chip in the table: short label + tone. Coaches/partners green, a coach
// application amber, staff dark, everyone else neutral. Role CHANGES live in
// the Edit user modal, not on the row.
const ROLE_CHIPS = {
  athlete: { label: 'Athlete' },
  base_user: { label: 'Base user' },
  player_parent: { label: 'Athlete/Parent' },
  coach: { label: 'Coach', tone: 'green' },
  basic_coach: { label: 'Coach (pending)', tone: 'amber' },
  partner: { label: 'Partner', tone: 'green' },
  guardian: { label: 'Guardian' },
  admin: { label: 'Admin', tone: 'dark' },
  internal_staff: { label: 'Staff', tone: 'dark' },
};
const roleChip = (role) => ROLE_CHIPS[role] || { label: ROLE_LABELS[role] || role || '—' };

const initialsOf = (u) => (
  `${(u.first_name || '').trim()[0] || ''}${(u.last_name || '').trim()[0] || ''}`.toUpperCase()
  || (u.email || '?')[0].toUpperCase()
);

// Feather trash-2, stroke 2 — replaces the old solid-red /bin.png pill.
const TrashIcon = ({ size = 16, strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// Human label for a bucket key (composition or channel:*). Falls back to the
// raw key if we don't recognize it (e.g. a new cohort key that just appeared).
const bucketLabel = (key) => {
  if (!key) return '';
  if (key === 'athlete_not_subscribed') return 'Athletes (not subscribed)';
  if (key === 'guardian_of_athlete') return 'Guardians of athletes';
  if (key === 'unknown_base_user') return 'Unknown base users';
  if (key.startsWith('channel:cohort:')) {
    return 'Cohort: ' + key.slice('channel:cohort:'.length);
  }
  if (key.startsWith('channel:')) {
    const k = key.slice('channel:'.length);
    if (k === 'google_oauth') return 'Channel: Google sign-in';
    if (k === 'apple_signin') return 'Channel: Apple sign-in';
    if (k === 'email_signup') return 'Channel: Email signup';
    if (k === 'admin_inputted') return 'Channel: Admin-created';
    if (k === 'concierge') return 'Channel: Concierge';
    // Mirrors channel_label() in backend/app/services/acquisition_channel.py —
    // paid/self-reported attribution outranks auth method, so these prefixed
    // keys need their own labels rather than falling through to the raw key.
    //
    // The suffix is either the advertiser's own utm_source ("meta") or a
    // source derived from an ad-platform click id ("google_ads", "meta_ads",
    // "tiktok_ads", "microsoft_ads" — see CLICK_ID_SOURCES). Both are rendered
    // the same way on purpose: the set is open-ended, so enumerating it here
    // would only create a fourth place for the ladder to drift.
    if (k.startsWith('paid:')) return 'Channel: Paid — ' + k.slice('paid:'.length);
    if (k.startsWith('self:')) return 'Channel: Self-reported — ' + k.slice('self:'.length);
    return 'Channel: ' + k;
  }
  return key;
};

// Server timestamps are UTC but serialized WITHOUT a tz suffix (e.g.
// "2026-06-16T17:39:30"). JS parses a tz-less datetime as LOCAL, showing the
// UTC wall-clock verbatim. Append 'Z' so it parses as UTC, then render local.
const parseServerDate = (iso) => {
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
};
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = parseServerDate(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = parseServerDate(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
};

// Password reset timeline rows (backend password_reset_events) in plain words.
const RESET_EVENT_LABELS = {
  sent: 'Reset email sent',
  send_failed: 'Reset email FAILED to send',
  delivered: 'Delivered to their mail server',
  bounced: 'Bounced',
  spam_complaint: 'Marked as spam',
  opened: 'Email opened',
  clicked: 'Link clicked in the email',
  link_opened: 'Reset page opened',
  link_expired: 'Opened an EXPIRED link',
  link_invalid: 'Opened an invalid link',
  completed: 'New password set',
  admin_reset: 'Password set by admin',
};

const resetEventNote = (ev) => {
  const d = ev.details || {};
  switch (ev.event) {
    case 'sent':
      return d.trigger === 'duplicate_register' ? 'after trying to sign up again' : '';
    case 'bounced':
    case 'spam_complaint':
      return d.Description || d.Name || '';
    case 'opened':
    case 'clicked':
      return [d.client, d.location].filter(Boolean).join(' · ');
    case 'link_opened':
    case 'link_expired':
      return d.token_age_min != null ? `${d.token_age_min} min after it was sent` : '';
    case 'admin_reset':
      return d.by_email ? `by ${d.by_email}` : '';
    default:
      return '';
  }
};

function ResetEmailHistory({ userId, API_URL }) {
  const [events, setEvents] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/admin/users/${userId}/password-reset-events`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((resp) => { if (!cancelled) setEvents(Array.isArray(resp.data) ? resp.data : []); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [userId, API_URL]);

  return (
    <div style={{ marginBottom: '20px' }} data-testid="admin_reset_email_history">
      <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Reset email history
      </h3>
      {failed ? (
        <p style={{ fontSize: '13px', color: '#64748b' }}>Could not load reset history.</p>
      ) : events === null ? (
        <p style={{ fontSize: '13px', color: '#64748b' }}>Loading…</p>
      ) : events.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#64748b' }}>No reset emails recorded.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '180px', overflowY: 'auto', fontSize: '13px' }}>
          {events.map((ev) => {
            const note = resetEventNote(ev);
            return (
              <li key={ev.id} style={{ padding: '4px 0', borderBottom: '1px solid #e2e8f0', color: '#1a202c' }}>
                <span style={{ color: '#64748b' }}>{fmtDateTime(ev.occurred_at)}</span>
                {' — '}
                {RESET_EVENT_LABELS[ev.event] || ev.event}
                {note && <span style={{ color: '#64748b' }}> ({note})</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function AdminUsersPage() {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState('');

  // Server-side pagination state
  const PAGE_SIZE = 100;
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Search & filter state — these go to the server, not the client.
  // `searchInput` is the live text box; `searchTerm` is the committed query
  // that actually hits the server. Text search runs only on Enter / the Search
  // button (like the /players search) — typing no longer auto-fetches.
  // Seeded from ?search= so a deep-link (see ?edit= below) lands pre-filtered.
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState(''); // '' | 'pending' | 'approved'
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState(''); // '' | 'yes' | 'no'
  // bucket filter is driven by ?bucket= URL param so home cards / report tabs
  // can deep-link into a filtered user list.
  const bucket = searchParams.get('bucket') || '';

  // Linked-accounts section state (Report C). Lazy-loads when edit modal opens.
  const [linkedAccounts, setLinkedAccounts] = useState(null);
  const [linkedLoading, setLinkedLoading] = useState(false);

  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);

  // Edit User modal state
  const [editUserData, setEditUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'athlete',
    is_active: true,
    user_source: 'prozpects_user',
    date_of_birth: ''
  });

  // Add user form states
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'athlete'
  });

  const getAxiosConfig = () => ({
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  // Initial load + redirect non-admins
  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'internal_staff') {
      navigate('/dashboard');
      return;
    }
    fetchUsers({ offset: 0, append: false });
  }, [user]);

  // Re-fetch when a committed search or a filter changes. Text search is
  // committed only on Enter / the Search button (searchTerm), so this no longer
  // fires per keystroke — dropdown filters still apply immediately. Skip the
  // very first render so mount doesn't double-fetch alongside the initial load
  // effect above.
  const didMountFilters = useRef(false);
  useEffect(() => {
    if (!didMountFilters.current) { didMountFilters.current = true; return; }
    if (user?.role !== 'admin' && user?.role !== 'internal_staff') return;
    fetchUsers({ offset: 0, append: false });
  }, [searchTerm, roleFilter, approvalFilter, emailVerifiedFilter, bucket]);

  // Deep-link: ?editStats=:userId used to open the edit-stats modal for that
  // user. That modal is retired — measurables are now edited inline on the
  // athlete-detail page — so old links land there instead.
  useEffect(() => {
    const editId = searchParams.get('editStats');
    if (!editId || !user) return;
    if (user.role !== 'admin' && user.role !== 'internal_staff') return;
    navigate(`/athletes/${editId}`, { replace: true });
  }, [searchParams, user]);

  // Deep-link: ?edit=:userId&search=:email, from the header search bar. Only
  // athletes have a profile page, so a non-athlete result (admin, partner,
  // coach, guardian) lands here instead and opens their Edit modal — the same
  // thing clicking them in this list does (openUserDetail).
  const editParam = searchParams.get('edit');
  const searchParam = searchParams.get('search');
  useEffect(() => {
    if (searchParam == null) return;
    setSearchInput(searchParam);
    setSearchTerm(searchParam);
  }, [searchParam]);
  useEffect(() => {
    if (!editParam) return;
    const target = users.find((u) => String(u.id) === editParam);
    if (!target) return;
    openEditUserModal(target);
    searchParams.delete('edit');
    setSearchParams(searchParams, { replace: true });
  }, [editParam, users]);

  const fetchUsers = async ({ offset = 0, append = false } = {}) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (roleFilter) params.set('role', roleFilter);
      if (approvalFilter) params.set('approval', approvalFilter);
      if (emailVerifiedFilter) params.set('email_verified', emailVerifiedFilter);
      if (bucket) params.set('bucket', bucket);
      params.set('offset', String(offset));
      params.set('limit', String(PAGE_SIZE));
      const response = await axios.get(
        `${API_URL}/admin/users?${params.toString()}`,
        getAxiosConfig()
      );
      // Server returns {items, total, has_more} when any param is given.
      const data = response.data;
      const items = Array.isArray(data) ? data : data.items || [];
      const tot = Array.isArray(data) ? data.length : (data.total || 0);
      const more = Array.isArray(data) ? false : !!data.has_more;
      setUsers(prev => append ? [...prev, ...items] : items);
      setTotal(tot);
      setHasMore(more);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage('Error loading users');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchUsers({ offset: users.length, append: true });
  };

  // Server already filtered — alias for the rest of the file.
  const filteredUsers = users;

  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u.id));

  // Server returns rows ordered by id desc (newest first). Client-side sort
  // across paginated data is misleading; deferred until server-side sort.

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.add(u.id));
        return next;
      });
    }
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, getAxiosConfig());
      setMessage(`${name} deleted`);
      setTimeout(() => setMessage(''), 3000);
      fetchUsers();
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected user${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API_URL}/admin/users/delete-bulk`, { user_ids: ids }, getAxiosConfig());
      const { deleted, errors } = res.data;
      setSelectedIds(new Set());
      setMessage(`Deleted ${deleted.length} user${deleted.length !== 1 ? 's' : ''}${errors.length ? ` (${errors.length} skipped)` : ''}`);
      setTimeout(() => setMessage(''), 4000);
      fetchUsers();
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const pendingCount = users.filter(u => !u.is_approved).length;
  const pendingCoachReviewCount = users.filter(u => u.role === 'basic_coach').length;

  // Edit User modal
  const openEditUserModal = (u) => {
    setEditUser(u);
    setEditUserData({
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      email: u.email || '',
      phone: u.phone || '',
      role: u.role || 'athlete',
      is_active: u.is_active,
      user_source: u.user_source || 'prozpects_user',
      date_of_birth: u.date_of_birth || ''
    });
    setShowEditUserModal(true);
    fetchLinkedAccounts(u.id);
  };

  const closeEditUserModal = () => {
    setShowEditUserModal(false);
    setEditUser(null);
    setLinkedAccounts(null);
  };

  // Lazy-load linked athletes/guardians for the currently-open edit modal.
  // Failures are quiet — the section just stays hidden if the API errors out
  // (we don't want a 500 on the linked endpoint to block editing the user).
  const fetchLinkedAccounts = async (userId) => {
    setLinkedLoading(true);
    setLinkedAccounts(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/admin/users/${userId}/linked`,
        getAxiosConfig()
      );
      setLinkedAccounts(data || { linked_athletes: [], linked_guardians: [] });
    } catch (err) {
      // 404 or other failure: keep section hidden
      setLinkedAccounts({ linked_athletes: [], linked_guardians: [] });
    } finally {
      setLinkedLoading(false);
    }
  };

  // Resolve a linked-account row to the FULL user object from the loaded list
  // (so the edit modal + login block get created_at / is_active / user_source /
  // phone, not blanks that a Save could overwrite). Falls back to a minimal
  // object only when the user isn't on the current page.
  const rowToUser = (row) => {
    const full = users.find((u) => u.id === row.user_id);
    if (full) return full;
    return {
      id: row.user_id,
      first_name: (row.name || '').split(' ')[0] || '',
      last_name: (row.name || '').split(' ').slice(1).join(' '),
      email: row.email || '',
      role: row.role || 'athlete',
    };
  };

  // Jump from a linked-row to the edit modal for that user. Resolves the full
  // user object from the already-loaded list so login block + editable fields
  // are populated from real data (not blanks that a Save could overwrite).
  const jumpToLinkedUser = (row) => {
    if (!row || !row.user_id) return;
    openEditUserModal(rowToUser(row));
  };

  // Clicking a linked athlete from a guardian's edit modal should open that
  // athlete's PROFILE (not their edit modal). Close the edit modal first so we
  // don't stack two modals.
  const jumpToLinkedAthlete = (row) => {
    if (!row || !row.user_id) return;
    closeEditUserModal();
    openDetailModal(rowToUser(row));
  };

  const saveUserInfo = async () => {
    try {
      const payload = {
        first_name: editUserData.first_name,
        last_name: editUserData.last_name,
        phone: editUserData.phone || null,
        role: editUserData.role,
        is_active: editUserData.is_active,
        user_source: editUserData.user_source,
        date_of_birth: editUserData.date_of_birth || null
      };
      if (!editUser.google_id) {
        payload.email = editUserData.email;
      }
      await axios.put(
        `${API_URL}/admin/users/${editUser.id}`,
        payload,
        getAxiosConfig()
      );
      setMessage('User updated successfully');
      closeEditUserModal();
      fetchUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setGeneratedPassword('');
    setShowGeneratedPassword(false);
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setNewPassword('');
    setGeneratedPassword('');
    setShowGeneratedPassword(false);
  };

  const openAddUserModal = () => {
    setNewUser({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'athlete'
    });
    setShowAddUserModal(true);
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    setNewUser({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'athlete'
    });
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.first_name || !newUser.last_name || !newUser.password) {
      setMessage('Error: Please fill in all required fields');
      return;
    }

    if (newUser.password !== newUser.confirmPassword) {
      setMessage('Error: Passwords do not match');
      return;
    }

    if (newUser.password.length < 8) {
      setMessage('Error: Password must be at least 8 characters');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/auth/register`,
        {
          email: newUser.email,
          first_name: newUser.first_name,
          last_name: newUser.last_name,
          phone: newUser.phone || null,
          password: newUser.password
        },
        getAxiosConfig()
      );

      if (newUser.role !== 'athlete') {
        const usersResponse = await axios.get(`${API_URL}/admin/users`, getAxiosConfig());
        const createdUser = usersResponse.data.find(u => u.email === newUser.email);

        if (createdUser) {
          await axios.put(
            `${API_URL}/admin/users/${createdUser.id}`,
            { role: newUser.role },
            getAxiosConfig()
          );
        }
      }

      setMessage(`User ${newUser.email} created successfully`);
      closeAddUserModal();
      fetchUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetPassword = async () => {
    if (!newPassword) {
      setMessage('Please enter a password');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/admin/users/${selectedUser.id}/reset-password`,
        { new_password: newPassword },
        getAxiosConfig()
      );
      setMessage(`Password reset for ${selectedUser.email}`);
      closePasswordModal();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const generatePassword = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/users/${selectedUser.id}/generate-password`,
        {},
        getAxiosConfig()
      );
      setGeneratedPassword(response.data.temporary_password);
      setNewPassword(response.data.temporary_password);
      setShowGeneratedPassword(true);
      setMessage('Password generated! Make sure to copy it.');
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const generatePasswordForNewUser = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser({ ...newUser, password: password, confirmPassword: password });
    setMessage('Password generated! Make sure to copy it.');
    setTimeout(() => setMessage(''), 3000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Password copied to clipboard');
    setTimeout(() => setMessage(''), 3000);
  };

  // Clicking a user row: athletes have an AthleteProfile → navigate to the
  // athlete-detail page. Everyone else (guardian/coach/partner/admin) has no
  // profile, so open the Edit modal instead, which shows their login activity
  // + clickable linked accounts.
  const openUserDetail = (u) => {
    if (isAthlete(u.role)) {
      openDetailModal(u);
    } else {
      openEditUserModal(u);
    }
  };

  // Athlete-detail: navigate to the canonical page (the old detail modal is retired).
  // The page itself renders an error state if the user has no AthleteProfile.
  const openDetailModal = (u) => {
    navigate(`/athletes/${u.id}`);
  };

  // Escape key handlers for modals
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeEditUserModal(); };
    if (showEditUserModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showEditUserModal]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeAddUserModal(); };
    if (showAddUserModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showAddUserModal]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closePasswordModal(); };
    if (showPasswordModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showPasswordModal]);

  const anyFilter = !!(searchInput || searchTerm || roleFilter || approvalFilter || emailVerifiedFilter);
  const subtitle = `${total.toLocaleString()} ${total === 1 ? 'account' : 'accounts'} · newest first`;
  const showingLabel = `Showing ${users.length.toLocaleString()}${total > 0 ? ` of ${total.toLocaleString()}` : ''}`
    + ((searchTerm || roleFilter || approvalFilter) ? ' (filtered)' : '');
  const addUserButton = (
    <button type="button" className="adm-btn adm-btn--primary" onClick={openAddUserModal}>+ Add user</button>
  );

  if (loading) {
    return (
      <AdminPage active="users" title="Users" actions={addUserButton}>
        <div className="adm__card"><div className="adm-loading">Loading users…</div></div>
      </AdminPage>
    );
  }

  const approveUser = async (u) => {
    try {
      if (u.role === 'basic_coach') {
        await axios.post(`${API_URL}/admin/coach-review/${u.id}/approve`, {}, getAxiosConfig());
        setMessage(`${u.first_name} ${u.last_name} approved as coach`);
        setTimeout(() => setMessage(''), 3000);
        fetchUsers();
      } else {
        await axios.put(`${API_URL}/admin/users/${u.id}`, { is_approved: true }, getAxiosConfig());
        setUsers(users.map(usr => usr.id === u.id ? { ...usr, is_approved: true } : usr));
        setMessage(`${u.first_name} ${u.last_name} approved`);
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <AdminPage active="users" title="Users" subtitle={subtitle} actions={addUserButton}>

      {message && (
        <div className={message.includes('Error') ? 'settings-toast settings-toast--error' : 'settings-toast'}
             style={{ position: 'relative', top: 'auto', right: 'auto', marginBottom: '16px' }}>
          {message}
        </div>
      )}

      {/* Needs action — one strip in place of the two stacked amber banners. */}
      {(pendingCoachReviewCount > 0 || pendingCount > 0) && (
        <div className="adm__card adm__card--compact adm-attention" data-testid="admin_users_needs_action">
          <span className="adm-attention__label">Needs action</span>
          {pendingCoachReviewCount > 0 && (
            <span className="adm-attention__item">
              <span className="adm-chip adm-chip--amber">{pendingCoachReviewCount}</span>
              coach application{pendingCoachReviewCount !== 1 ? 's' : ''}
              <button type="button" className="adm-link" onClick={() => navigate('/admin/approvals')}>
                Review in Approvals →
              </button>
            </span>
          )}
          {pendingCount > 0 && (
            <span className="adm-attention__item">
              <span className="adm-chip adm-chip--amber">{pendingCount}</span>
              account{pendingCount !== 1 ? 's' : ''} pending approval
              <button
                type="button"
                className="adm-link"
                onClick={() => setApprovalFilter(approvalFilter === 'pending' ? '' : 'pending')}
              >
                {approvalFilter === 'pending' ? 'Show everyone →' : 'Show only pending →'}
              </button>
            </span>
          )}
        </div>
      )}

      {/* Bucket chip — deep-linked filter from home cards / report tabs.
          Coexists with the search/role/approval filters below; clicking ×
          drops just the bucket via navigate. */}
      {bucket && (
        <div className="adm-bucket">
          <span>Filtered by:</span>
          <span className="adm-chip adm-chip--green adm-bucket__chip">
            {bucketLabel(bucket)}
            <button
              type="button"
              className="adm-iconbtn"
              onClick={() => {
                searchParams.delete('bucket');
                setSearchParams(searchParams, { replace: true });
              }}
              aria-label="Clear bucket filter"
            >
              ×
            </button>
          </span>
        </div>
      )}

      {/* Filters — all server-side. Text search commits on Enter. */}
      <div className="adm-toolbar">
        <label className="adm-field adm-field--grow">
          <span className="adm-field__label">Search</span>
          <input
            className="adm-input"
            type="text"
            placeholder="Name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setSearchTerm(searchInput.trim()); }}
          />
        </label>
        <label className="adm-field adm-field--w160">
          <span className="adm-field__label">Role</span>
          <select className="adm-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All roles</option>
            <option value="athlete">Athlete</option>
            <option value="guardian">Guardian/Parent</option>
            <option value="coach">Coach</option>
            <option value="basic_coach">Coach (Pending)</option>
            <option value="partner">Partner</option>
            <option value="admin">Admin</option>
            <option value="internal_staff">Internal Staff</option>
          </select>
        </label>
        <label className="adm-field adm-field--w150">
          <span className="adm-field__label">Approval</span>
          <select className="adm-select" value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)}>
            <option value="">Any</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
          </select>
        </label>
        <label className="adm-field adm-field--w160">
          <span className="adm-field__label">Email</span>
          <select className="adm-select" value={emailVerifiedFilter} onChange={(e) => setEmailVerifiedFilter(e.target.value)}>
            <option value="">Any</option>
            <option value="yes">Verified</option>
            <option value="no">Not verified</option>
          </select>
        </label>
        {anyFilter && (
          <button
            type="button"
            className="adm-btn"
            onClick={() => {
              setSearchInput('');
              setSearchTerm('');
              setRoleFilter('');
              setApprovalFilter('');
              setEmailVerifiedFilter('');
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="adm__card adm__card--flush">
        {selectedIds.size > 0 ? (
          <div className="adm-selhead">
            <span className="adm-selhead__count">{selectedIds.size} selected</span>
            <button type="button" className="adm-btn adm-btn--danger adm-btn--sm" onClick={deleteSelected}>
              <TrashIcon size={13} strokeWidth={2.2} />
              Delete selected
            </button>
            <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setSelectedIds(new Set())}>
              Clear
            </button>
          </div>
        ) : (
          <div className="adm-card__head adm-card__head--table">
            <h2 className="adm-card__title">All users</h2>
            <span className="adm-card__meta">{showingLabel}</span>
          </div>
        )}

        <div className="adm-tablewrap">
          <table className="adm-table adm-table--middle">
            <thead>
              <tr>
                <th className="adm-col--check">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="adm-col--user">User</th>
                <th>Role</th>
                <th>School</th>
                <th className="adm-col--date">Last login</th>
                <th className="adm-col--actions"><span className="adm-sr">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                const selected = selectedIds.has(u.id);
                const chip = roleChip(u.role);
                const lastLogin = fmtDateTime(u.last_login_at);
                return (
                  <tr
                    key={u.id}
                    className={`adm-tr--link${selected ? ' adm-tr--selected' : ''}`}
                    onClick={(e) => {
                      // Whole-row click opens the user, but clicks on a
                      // control (checkbox, action buttons) keep their own job.
                      if (e.target.closest('button, select, input, a')) return;
                      openUserDetail(u);
                    }}
                    title="View details"
                  >
                    <td className="adm-col--check">
                      <input
                        type="checkbox"
                        aria-label={`Select ${name}`}
                        checked={selected}
                        onChange={() => toggleSelect(u.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="adm-col--user">
                      <div className="adm-usercell">
                        <span className="adm-avatar" aria-hidden="true">{initialsOf(u)}</span>
                        <div className="adm-usercell__text">
                          <div className="adm-usercell__name">
                            <span className="adm-link adm-cell__name">{name}</span>
                            {!u.is_approved && <span className="adm-chip adm-chip--amber">Pending approval</span>}
                          </div>
                          <div className="adm-cell__meta">
                            {/* Break at the @ only, so a long address wraps as
                                local / @domain rather than a letter at a time. */}
                            {(u.email || '').split('@').map((part, i) => (
                              <React.Fragment key={i}>{i > 0 && <><wbr />@</>}{part}</React.Fragment>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="adm-col--nowrap">
                      <span className={`adm-chip${chip.tone ? ` adm-chip--${chip.tone}` : ''}`}>{chip.label}</span>
                    </td>
                    <td className="adm-col--school">{u.school || <span className="adm-no">—</span>}</td>
                    <td className="adm-col--date">
                      {lastLogin || <span className="adm-never">Never</span>}
                    </td>
                    <td className="adm-col--actions" onClick={(e) => e.stopPropagation()}>
                      <div className="adm-rowactions">
                        {(!u.is_approved || u.role === 'basic_coach') && (
                          <button
                            type="button"
                            className="adm-btn adm-btn--primary adm-btn--sm"
                            onClick={() => approveUser(u)}
                          >
                            Approve
                          </button>
                        )}
                        <button
                          type="button"
                          className="adm-iconbtn"
                          onClick={() => openPasswordModal(u)}
                          disabled={!!u.google_id}
                          title={u.google_id ? 'Signed in with Google — no password to reset' : 'Reset password'}
                          aria-label="Reset password"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="8" cy="15" r="4" />
                            <line x1="10.85" y1="12.15" x2="19" y2="4" />
                            <line x1="18" y1="5" x2="20.5" y2="7.5" />
                            <line x1="15" y1="8" x2="17.5" y2="10.5" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="adm-iconbtn"
                          onClick={() => openEditUserModal(u)}
                          title="Edit user"
                          aria-label="Edit user"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="adm-iconbtn adm-iconbtn--danger"
                          onClick={() => deleteUser(u.id, name)}
                          title="Delete user"
                          aria-label="Delete user"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && <div className="adm-empty">No users match these filters.</div>}

        <div className="adm-pager">
          <span>{showingLabel}</span>
          {hasMore && (
            <button type="button" className="adm-btn" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>


      {/* Edit User Modal */}
      {showEditUserModal && editUser && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="edit-user-modal-title" onClick={closeEditUserModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 className="modal-title" id="edit-user-modal-title" style={{ marginBottom: '24px' }}>Edit User</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">First Name</label>
                <input
                  className="form-field__input"
                  type="text"
                  value={editUserData.first_name}
                  onChange={(e) => setEditUserData({ ...editUserData, first_name: e.target.value })}
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Last Name</label>
                <input
                  className="form-field__input"
                  type="text"
                  value={editUserData.last_name}
                  onChange={(e) => setEditUserData({ ...editUserData, last_name: e.target.value })}
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Email</label>
                <input
                  className="form-field__input"
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                  disabled={!!editUser.google_id}
                />
                {editUser.google_id && (
                  <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Email managed by Google</p>
                )}
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Phone</label>
                <input
                  className="form-field__input"
                  type="tel"
                  value={editUserData.phone}
                  onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })}
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Date of Birth</label>
                <input
                  className="form-field__input"
                  type="date"
                  value={editUserData.date_of_birth}
                  onChange={(e) => setEditUserData({ ...editUserData, date_of_birth: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Role</label>
                <select
                  className="form-field__select"
                  value={editUserData.role}
                  onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                >
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                  <option value="partner">Partner</option>
                  <option value="admin">Admin</option>
                  <option value="internal_staff">Internal Staff</option>
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Source</label>
                <select
                  className="form-field__select"
                  value={editUserData.user_source}
                  onChange={(e) => setEditUserData({ ...editUserData, user_source: e.target.value })}
                >
                  <option value="prozpects_user">Self-Registered</option>
                  <option value="admin_inputted">Admin Created</option>
                  <option value="concierge">Concierge</option>
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Status</label>
                <div style={{ display: 'flex', alignItems: 'center', height: '42px', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={editUserData.is_active}
                    onChange={(e) => setEditUserData({ ...editUserData, is_active: e.target.checked })}
                    id="edit-user-active"
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="edit-user-active" style={{ fontSize: '14px', color: '#1a202c', cursor: 'pointer' }}>
                    Active
                  </label>
                </div>
              </div>
            </div>

            {/* Login activity — from the full user row (real /admin/users rows
                only; linked-row fakeUsers have no created_at, so guard on it). */}
            {editUser && editUser.created_at && (
              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Login activity
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '13px' }}>
                  <div><span style={{ color: '#64748b' }}>Signed up: </span>{fmtDate(editUser.created_at) || '—'}</div>
                  <div><span style={{ color: '#64748b' }}>Logins: </span>{editUser.login_count ?? 0}</div>
                  <div><span style={{ color: '#64748b' }}>First login: </span>{fmtDate(editUser.first_login_at) || 'Never'}</div>
                  <div><span style={{ color: '#64748b' }}>Last login: </span>{fmtDateTime(editUser.last_login_at) || '—'}</div>
                  <div><span style={{ color: '#64748b' }}>Last active: </span>{fmtDateTime(editUser.last_active_at) || '—'}</div>
                </div>
              </div>
            )}

            {/* Linked accounts (Report C) — lazy-loaded when modal opens.
                Hidden when both arrays are empty so most users see no extra
                noise. `named_no_account` guardians render grayed out — they
                came from the denormalized guardian1 / guardian2 fields and
                don't have their own User row to click into. */}
            {linkedAccounts && (linkedAccounts.linked_athletes?.length > 0 || linkedAccounts.linked_guardians?.length > 0) && (
              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                {linkedAccounts.linked_athletes?.length > 0 && (
                  <div style={{ marginBottom: linkedAccounts.linked_guardians?.length > 0 ? '20px' : 0 }}>
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Linked athletes ({linkedAccounts.linked_athletes.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {linkedAccounts.linked_athletes.map((row, idx) => (
                        <Button
                          key={row.user_id || `a-${idx}`}
                          type="button"
                          variant="secondary"
                          fullWidth
                          onClick={() => row.user_id && jumpToLinkedAthlete(row)}
                          disabled={!row.user_id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '10px 12px', textAlign: 'left',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a' }}>
                              {row.name || row.email || '—'}
                              {row.relation && (
                                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  {row.relation}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                              {row.email || ''}
                              {row.role && <> · {ROLE_LABELS[row.role] || row.role}</>}
                              {row.subscription_status && row.subscription_status !== 'none' && <> · {row.subscription_status}</>}
                            </div>
                          </div>
                          {row.user_id && <span style={{ color: '#94a3b8', fontSize: '16px' }}>›</span>}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {linkedAccounts.linked_guardians?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Linked guardians ({linkedAccounts.linked_guardians.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {linkedAccounts.linked_guardians.map((row, idx) => {
                        const noAccount = row.source === 'named_no_account' || !row.user_id;
                        return (
                          <Button
                            key={row.user_id || `g-${idx}`}
                            type="button"
                            variant="secondary"
                            fullWidth
                            onClick={() => !noAccount && jumpToLinkedUser(row)}
                            disabled={noAccount}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 12px', textAlign: 'left',
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '14px', color: noAccount ? '#64748b' : '#0f172a' }}>
                                {row.name || row.email || '—'}
                                {noAccount && (
                                  <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 500, color: '#94a3b8' }}>
                                    (no account)
                                  </span>
                                )}
                                {row.relation && (
                                  <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {row.relation}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                {row.email || ''}
                                {row.role && <> · {ROLE_LABELS[row.role] || row.role}</>}
                              </div>
                            </div>
                            {!noAccount && <span style={{ color: '#94a3b8', fontSize: '16px' }}>›</span>}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {linkedLoading && (
              <div style={{ marginTop: '20px', fontSize: '12px', color: '#94a3b8' }}>
                Loading linked accounts…
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '24px' }}>
              <Button variant="secondary" onClick={closeEditUserModal}>
                Cancel
              </Button>
              <Button variant="accent" onClick={saveUserInfo}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-user-modal-title" onClick={closeAddUserModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 className="modal-title" id="add-user-modal-title" style={{ marginBottom: '24px' }}>Add New User</h2>

            <div className="form-field">
              <label className="form-field__label">Email *</label>
              <input
                className="form-field__input"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>

            <div className="form-row">
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">First Name *</label>
                <input
                  className="form-field__input"
                  type="text"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Last Name *</label>
                <input
                  className="form-field__input"
                  type="text"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="form-row" style={{ marginTop: '16px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Phone</label>
                <input
                  className="form-field__input"
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="555-1234"
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-field__label">Role *</label>
                <select
                  className="form-field__select"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                  <option value="partner">Partner</option>
                  <option value="admin">Admin</option>
                  <option value="internal_staff">Internal Staff</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Button
                variant="accent"
                fullWidth
                onClick={generatePasswordForNewUser}
              >
                Generate Random Password
              </Button>
            </div>

            <div className="form-field">
              <label className="form-field__label">Password *</label>
              <input
                className="form-field__input"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Min 8 chars, uppercase, lowercase, symbol"
              />
            </div>

            <div className="form-field">
              <label className="form-field__label">Confirm Password *</label>
              <input
                className="form-field__input"
                type="text"
                value={newUser.confirmPassword}
                onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                placeholder="Re-enter password"
              />
            </div>

            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '-8px' }}>
              Password must be 8+ characters with uppercase, lowercase, and a symbol
            </p>

            <div className="modal-actions">
              <Button variant="secondary" onClick={closeAddUserModal}>
                Cancel
              </Button>
              <Button variant="accent" onClick={createUser}>
                Create User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="password-modal-title" onClick={closePasswordModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 className="modal-title" id="password-modal-title" style={{ marginBottom: '24px' }}>Reset Password</h2>

            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#475569' }}>
              <strong>User:</strong> {selectedUser.first_name} {selectedUser.last_name}
              <br />
              <strong>Email:</strong> {selectedUser.email}
            </div>

            <ResetEmailHistory userId={selectedUser.id} API_URL={API_URL} />

            {selectedUser.google_id ? (
              <div className="billing-upgrade-card" style={{ textAlign: 'left', padding: '16px', marginBottom: '16px' }}>
                This user signed up with Google. They don't have a password.
                <br />They must sign in using "Sign in with Google" button.
              </div>
            ) : (
              <>
                <div className="billing-upgrade-card" style={{ textAlign: 'left', padding: '16px', marginBottom: '16px' }}>
                  You can either enter a custom password or generate a random secure password.
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <Button
                    variant="accent"
                    fullWidth
                    onClick={generatePassword}
                  >
                    Generate Random Password
                  </Button>
                </div>

                {showGeneratedPassword && (
                  <div
                    style={{
                      padding: '16px',
                      background: 'rgba(77,128,100,0.10)',
                      border: '2px solid #4D8064',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      fontFamily: 'monospace',
                      fontSize: '15px',
                      wordBreak: 'break-all',
                      cursor: 'pointer',
                      color: '#1a202c'
                    }}
                    onClick={() => copyToClipboard(generatedPassword)}
                    title="Click to copy"
                  >
                    <strong style={{ fontSize: '13px', fontFamily: 'inherit', color: '#475569' }}>
                      Generated Password (click to copy):
                    </strong>
                    <br />
                    {generatedPassword}
                  </div>
                )}

                <div className="form-field">
                  <label className="form-field__label">
                    Or Enter Custom Password:
                  </label>
                  <input
                    className="form-field__input"
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 chars)"
                  />
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Must be 8+ characters with uppercase, lowercase, and a symbol
                  </p>
                </div>
              </>
            )}

            <div className="modal-actions">
              <Button variant="secondary" onClick={closePasswordModal}>
                Cancel
              </Button>
              {!selectedUser.google_id && (
                <Button
                  variant="primary"
                  onClick={resetPassword}
                  disabled={!newPassword}
                >
                  Reset Password
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

    </AdminPage>
  );
}
